import { use } from 'react';

import { ConfirmContext, type ConfirmFunction } from './ConfirmContext';

/**
 * Asks before something that cannot be quietly undone, and waits for the answer.
 *
 * Replaces `window.confirm` one for one: where the browser's dialog blocked and
 * returned a boolean, this resolves a promise.
 *
 * ```ts
 * const confirm = useConfirm();
 *
 * // destructive: resolves true when confirmed
 * if (!(await confirm({ tier: 'destructive', title: 'Remove HV-100?', consequence: '…', confirmLabel: 'Remove item' }))) return;
 *
 * // terminal: resolves the reason, or null when the person backed out
 * const reason = await confirm({ tier: 'terminal', title: 'Withdraw INV-42?', consequence: '…', confirmLabel: 'Withdraw invoice' });
 * if (reason === null) return;
 * ```
 *
 * The returned function never changes identity, so it is safe in dependency lists.
 *
 * @returns the function that asks
 * @throws Error when used outside a ConfirmProvider, which is a wiring mistake
 *     rather than a runtime condition worth handling
 */
export function useConfirm(): ConfirmFunction {
  const confirm = use(ConfirmContext);
  if (confirm === null) {
    throw new Error('useConfirm must be called inside a ConfirmProvider');
  }
  return confirm;
}
